import React, { useState, useEffect } from "react";
import { Car } from "@/types/fleet";
import { parseCSV } from "@/utils/csvParser";
import { DASHeader } from "@/components/DASHeader";
import { DASHero } from "@/components/DASHero";
import { DASFleetCard } from "@/components/DASFleetCard";
import { DASBookingModal } from "@/components/DASBookingModal";
import { DASDetailsSheet } from "@/components/DASDetailsSheet";
import { DASHowItWorks } from "@/components/DASHowItWorks";
import { DASPricing } from "@/components/DASPricing";
import { DASFAQ } from "@/components/DASFAQ";
import { DASContact } from "@/components/DASContact";
import { DASFooter } from "@/components/DASFooter";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { useToast } from "@/hooks/use-toast";
import { trackViewFleet } from "@/utils/analytics";
import { useUserJourney } from "@/hooks/useUserJourney";

function App() {
  const [cars, setCars] = useState<Car[]>([]);
  const [services, setServices] = useState<Car[]>([]);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isDetailsSheetOpen, setIsDetailsSheetOpen] = useState(false);
  const { toast } = useToast();
  const { addViewedCar } = useUserJourney();

  useEffect(() => {
    const loadCars = async () => {
      try {
        const response = await fetch('/fleet.csv');
        const csvText = await response.text();
        const carsData = await parseCSV(csvText);

        // Filter active items and sort by display_order
        const activeItems = carsData
          .filter((car: Car) => car.active)
          .sort((a: Car, b: Car) => a.display_order - b.display_order);

        // Separate vehicles and services
        const vehicles = activeItems.filter((item: Car) => item.type !== "Service");
        const serviceItems = activeItems.filter((item: Car) => item.type === "Service");

        setCars(vehicles);
        setServices(serviceItems);
        trackViewFleet();
      } catch (error) {
        toast({
          title: "Fehler beim Laden der Fahrzeuge",
          description: "Bitte laden Sie die Seite neu.",
          variant: "destructive",
        });
      }
    };

    loadCars();
  }, [toast]);

  const handleRentClick = (car: Car) => {
    setSelectedCar(car);
    setIsBookingModalOpen(true);
  };

  const handleDetailsClick = (car: Car) => {
    setSelectedCar(car);
    setIsDetailsSheetOpen(true);
    addViewedCar(car.title);
  };

  // Schema.org structured data
  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "AutoRental",
    "name": "DAS Group AG",
    "image": "https://dasgroup.ch/logo.png",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bahnhofstrasse 8B",
      "addressLocality": "Baar",
      "postalCode": "6340",
      "addressCountry": "CH"
    },
    "telephone": "+41414402223",
    "priceRange": "CHF 60-220",
    "areaServed": {
      "@type": "Country",
      "name": "Switzerland"
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
      />
      {[...cars, ...services].map((car) => (
        <script
          key={car.id}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": car.title,
              "description": car.description,
              "image": car.image_hero,
              "offers": {
                "@type": "Offer",
                "price": car.price_per_day_chf || "0",
                "priceCurrency": "CHF",
                "availability": "https://schema.org/InStock",
                "areaServed": {
                  "@type": "Country",
                  "name": "Switzerland"
                }
              }
            })
          }}
        />
      ))}

      <div className="min-h-screen bg-white">
        <DASHeader />

        <main className="pt-16">
          <DASHero />

          <section id="cars-section" className="py-12 md:py-20 bg-white">
            <div className="container mx-auto px-4">
              <div className="text-center mb-8 md:mb-12">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-black mb-4">
                  Unsere Fahrzeugflotte
                </h2>
                <p className="text-lg md:text-xl text-gray-700">
                  Gepflegte Fahrzeuge für jeden Bedarf
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {cars.map((car) => (
                  <DASFleetCard
                    key={car.id}
                    car={car}
                    onRentClick={handleRentClick}
                    onDetailsClick={handleDetailsClick}
                  />
                ))}
              </div>
            </div>
          </section>

          {services.length > 0 && (
            <section id="services-section" className="py-12 md:py-20 bg-gray-50">
              <div className="container mx-auto px-4">
                <div className="text-center mb-8 md:mb-12">
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-black mb-4">
                    Chauffeur Services
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700">
                    Premium-Service für besondere Anlässe
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {services.map((service) => (
                    <DASFleetCard
                      key={service.id}
                      car={service}
                      onRentClick={handleRentClick}
                      onDetailsClick={handleDetailsClick}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          <DASHowItWorks />
          <DASPricing />
          <DASFAQ />
          <DASContact />
        </main>

        <DASFooter />

        <DASBookingModal
          car={selectedCar}
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
        />

        <DASDetailsSheet
          car={selectedCar}
          isOpen={isDetailsSheetOpen}
          onClose={() => setIsDetailsSheetOpen(false)}
          onRentClick={handleRentClick}
        />

        <Toaster />
        <SonnerToaster />
      </div>
    </>
  );
}

export default App;
